'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';

import { Trans, useTranslation } from 'react-i18next';

import { CreateDonationRequestModeEnum, DonationMatching, Subproject } from '@/app/api';

import CheckIcon from '@/app/components/CheckIcon/CheckIcon';
import { DonateBoxButtons } from '@/app/components/DonateBoxDobro/components/DonateBoxButtons';
import { DonationVerifiedByDonioIcon } from '@/app/components/DonationVeryfiedByDonioIcon/DonationVeryfiedByDonioIcon';
import { MatchingBox } from '@/app/components/MatchingBox/MatchingBox';
import TabsSelect, { TabsSelectOption } from '@/app/components/TabsSelect/TabsSelect';
import { useIsUserInNewHeaderLayoutGroup } from '@/app/hooks/useIsUserInNewHeaderLayoutGroup';
import { DonateBoxContainer } from '@/app/components/DonateBoxDobro/components/DonateButtonsContainer';
interface DonateBoxProps extends PropsWithClassname {
  donateLink: string;
  selectedDonationMode: CreateDonationRequestModeEnum;
  onChangeSelectedDonationMode: (nextDonationMode: CreateDonationRequestModeEnum) => void;
  donationModeOptions: TabsSelectOption<CreateDonationRequestModeEnum>[];
  isProjectLongTerm: boolean;
  onShareButtonClick: () => void;
  onVerificationNoteClick: () => void;
  subProject?: Subproject;
  activeGoodHabitAmount?: number;
  matching?: DonationMatching;
  heading: string;
}

const DonateBoxDobro = ({
  donateLink,
  donationModeOptions,
  selectedDonationMode,
  isProjectLongTerm,
  onChangeSelectedDonationMode,
  onShareButtonClick,
  onVerificationNoteClick,
  className,
  subProject,
  activeGoodHabitAmount,
  matching,
  heading,
}: DonateBoxProps) => {
  const isUserInNewHeaderLayoutGroup = useIsUserInNewHeaderLayoutGroup();
  const { t } = useTranslation();

  return (
    <div
      className={classNames(
        className,
        'lg:bg-white lg:rounded-[4px] lg:border lg:border-neutral-light lg:shadow-md lg:py-[32px] lg:px-[32px]',
      )}
    >
      <h2 className="hidden lg:inline-block text-center text-[20px] font-medium leading-[24px] text-balance mt-0">
        {heading}
      </h2>
      {isProjectLongTerm && !subProject && (
        <TabsSelect<CreateDonationRequestModeEnum>
          value={selectedDonationMode}
          onChange={onChangeSelectedDonationMode}
          options={donationModeOptions}
          className="my-[16px]"
        />
      )}
      <div className="block lg:hidden">
        <DonateBoxContainer
          className="py-[20px] lg:py-[12px]"
          donateLink={donateLink}
          activeGoodHabitAmount={activeGoodHabitAmount ?? 0}
          onShareButtonClick={onShareButtonClick}
        />
      </div>
      <div className="hidden lg:block">
        <DonateBoxButtons
          donateButtonTestId="desktop-static-donate-button"
          shareButtonText={
            isUserInNewHeaderLayoutGroup ? t('components.DonateBox.shareStory') : t('components.DonateBox.share')
          }
          className="py-[20px] lg:py-[12px]"
          donateLink={donateLink}
          activeGoodHabitAmount={activeGoodHabitAmount ?? 0}
          onShareButtonClick={onShareButtonClick}
        />
      </div>
      {!subProject && !isUserInNewHeaderLayoutGroup && (
        <div className="text-center">
          <CheckIcon className="text-success-mid inline w-[24px] mr-[4px]" />
          <Trans i18nKey="components.DonateBox.verification">
            Všechny sbírky{' '}
            <button
              className="inline underline hover:text-primary-mid hover:no-underline"
              onClick={onVerificationNoteClick}
            >
              ověřujeme
            </button>
          </Trans>
        </div>
      )}
      {!subProject && isUserInNewHeaderLayoutGroup && (
        <div className="text-center">
          <DonationVerifiedByDonioIcon className="text-success-mid inline mr-[12px]" />

          <Trans i18nKey="components.DonateBox.storyVerifiedByDonio">
            Sbírka
            <button
              className="inline underline text-primary-mid hover:text-primary-mid hover:no-underline"
              onClick={onVerificationNoteClick}
            >
              ověřená
            </button>
            donio
          </Trans>
        </div>
      )}
      {matching && <MatchingBox matching={matching} />}
    </div>
  );
};

export default DonateBoxDobro;
